import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.tsx';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { Badge } from '../ui/badge.tsx';
import { supabase } from '../../supabase';
import { useAuth } from '../../AuthContext';
import { getWatchlistLimit } from '../../utils/tiers';
import {
  FiPlus,
  FiTrash2,
  FiEdit2,
  FiSave,
  FiX,
  FiSearch,
  FiList,
  FiAlertCircle,
  FiCopy
} from "react-icons/fi";

const DEFAULT_ASSET_CLASS = 'stocks';
const ASSET_CLASS_OPTIONS = [
  { value: 'stocks', label: 'Stocks' },
  { value: 'etfs', label: 'ETFs' },
  { value: 'crypto', label: 'Crypto' },
  { value: 'commodities', label: 'Commodities' }
];

const normalizeAssetClass = (value) => {
  const normalized = String(value || '').toLowerCase().trim();
  if (['stock', 'stocks', 'equity', 'equities'].includes(normalized)) return 'stocks';
  if (['etf', 'etfs'].includes(normalized)) return 'etfs';
  if (['crypto', 'cryptos', 'cryptoasset', 'cryptoassets'].includes(normalized)) return 'crypto';
  if (['commodity', 'commodities'].includes(normalized)) return 'commodities';
  return DEFAULT_ASSET_CLASS;
};

const getAssetClassLabel = (assetClass) => {
  const normalized = normalizeAssetClass(assetClass);
  return ASSET_CLASS_OPTIONS.find(option => option.value === normalized)?.label || 'Assets';
};

const WatchlistEditor = ({ embedded = false }) => {
  const { billingTier } = useAuth() || {};
  const navigate = useNavigate();
  const [watchlists, setWatchlists] = useState([]);
  const [symbols, setSymbols] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const watchlistLimit = getWatchlistLimit(billingTier || 'free');
  const watchlistUsage = watchlists.length;
  const isAtLimit = watchlistUsage >= watchlistLimit;

  // Form states
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    asset_class: DEFAULT_ASSET_CLASS,
    symbols: []
  });
  
  // Search states
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSymbols, setFilteredSymbols] = useState([]);
  const [bulkSymbolsInput, setBulkSymbolsInput] = useState('');
  const [bulkMissing, setBulkMissing] = useState([]);

  // API base URL
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4998';

  // Fetch watchlists
  const fetchWatchlists = async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');
      
      const response = await fetch(`${API_BASE}/api/watchlists`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch watchlists');
      const data = await response.json();
      const normalized = (data.watchlists || []).map((watchlist) => ({
        ...watchlist,
        asset_class: normalizeAssetClass(watchlist.asset_class)
      }));
      setWatchlists(normalized);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch symbols
  const fetchSymbols = async (assetClass = formData.asset_class) => {
    try {
      const normalizedAssetClass = normalizeAssetClass(assetClass);
      const query = normalizedAssetClass ? `?asset_class=${encodeURIComponent(normalizedAssetClass)}` : '';
      const response = await fetch(`${API_BASE}/api/symbols${query}`);
      if (!response.ok) throw new Error('Failed to fetch symbols');
      const data = await response.json();
      setSymbols(data.symbols || []);
      setFilteredSymbols(data.symbols || []);
    } catch (err) {
      setError(err.message);
    }
  };

  // Search symbols
  useEffect(() => {
    if (!searchTerm) {
      setFilteredSymbols(symbols);
    } else {
      const filtered = symbols.filter(symbol =>
        (symbol.ticker?.toLowerCase() ?? '').includes(searchTerm.toLowerCase()) ||
        (symbol.company_name?.toLowerCase() ?? '').includes(searchTerm.toLowerCase()) ||
        (symbol.name?.toLowerCase() ?? '').includes(searchTerm.toLowerCase()) ||
        (symbol.sector?.toLowerCase() ?? '').includes(searchTerm.toLowerCase()) ||
        (symbol.exchange?.toLowerCase() ?? '').includes(searchTerm.toLowerCase())
      );
      setFilteredSymbols(filtered);
    }
  }, [searchTerm, symbols]);

  // Load data on mount
  useEffect(() => {
    fetchWatchlists();
    fetchSymbols(DEFAULT_ASSET_CLASS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showForm) return;
    fetchSymbols(formData.asset_class);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.asset_class, showForm]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const url = editingId
        ? `${API_BASE}/api/watchlists/${editingId}`
        : `${API_BASE}/api/watchlists`;

      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          ...formData,
          asset_class: normalizeAssetClass(formData.asset_class)
        })
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || 'Failed to save watchlist');
      }

      // Handle LIFO override notification
      if (responseData.limit_reached) {
        const msg = `Watchlist "${responseData.oldest_deleted}" was removed to stay within your ${responseData.limit}-watchlist limit.`;
        console.log(msg);
        // Optionally show this as a toast/notification
      }

      await fetchWatchlists();
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this watchlist?')) return;
    
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const response = await fetch(`${API_BASE}/api/watchlists/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      if (!response.ok) throw new Error('Failed to delete watchlist');
      
      await fetchWatchlists();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (watchlist) => {
    setFormData({
      name: watchlist.name,
      description: watchlist.description,
      asset_class: normalizeAssetClass(watchlist.asset_class),
      symbols: watchlist.symbols
    });
    setBulkSymbolsInput('');
    setBulkMissing([]);
    setEditingId(watchlist.id);
    setShowForm(true);
  };

  // Handle duplicate
  const handleDuplicate = async (watchlist) => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const newWatchlist = {
        name: `Copy of ${watchlist.name}`,
        description: watchlist.description,
        asset_class: normalizeAssetClass(watchlist.asset_class),
        symbols: watchlist.symbols
      };

      const response = await fetch(`${API_BASE}/api/watchlists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify(newWatchlist)
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || 'Failed to duplicate watchlist');
      }

      // Handle LIFO override notification
      if (responseData.limit_reached) {
        const msg = `Watchlist "${responseData.oldest_deleted}" was removed to stay within your ${responseData.limit}-watchlist limit.`;
        console.log(msg);
      }

      await fetchWatchlists();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({ name: '', description: '', asset_class: DEFAULT_ASSET_CLASS, symbols: [] });
    setEditingId(null);
    setShowForm(false);
    setBulkSymbolsInput('');
    setBulkMissing([]);
  };

  // Add symbol to watchlist
  const addSymbol = (symbol) => {
    if (!formData.symbols.find(s => s.ticker === symbol.ticker)) {
      setFormData(prev => ({
        ...prev,
        symbols: [...prev.symbols, symbol]
      }));
    }
  };

  // Remove symbol from watchlist
  const removeSymbol = (ticker) => {
    setFormData(prev => ({
      ...prev,
      symbols: prev.symbols.filter(s => s.ticker !== ticker)
    }));
  };

  const parseBulkSymbols = (input) => {
    return input
      .split(/[\s,;]+/)
      .map((token) => token.trim())
      .filter(Boolean);
  };

  const handleBulkAdd = () => {
    const requested = parseBulkSymbols(bulkSymbolsInput);
    if (requested.length === 0) {
      setBulkMissing([]);
      return;
    }

    const symbolMap = new Map(
      symbols.map((symbol) => [String(symbol.ticker || '').toUpperCase(), symbol])
    );
    const existingTickers = new Set(
      formData.symbols.map((symbol) => String(symbol.ticker || '').toUpperCase())
    );
    const missing = [];
    const toAdd = [];

    requested.forEach((tickerRaw) => {
      const normalized = String(tickerRaw || '').toUpperCase();
      if (!normalized) return;
      const match = symbolMap.get(normalized);
      if (match) {
        if (!existingTickers.has(normalized)) {
          existingTickers.add(normalized);
          toAdd.push(match);
        }
      } else if (!missing.includes(normalized)) {
        missing.push(normalized);
      }
    });

    if (toAdd.length > 0) {
      setFormData(prev => ({
        ...prev,
        symbols: [...prev.symbols, ...toAdd]
      }));
    }
    setBulkMissing(missing);
  };

  const HeadingTag = embedded ? 'h2' : 'h1';
  const actionButtonSize = embedded ? 'md' : 'lg';

  return (
    <div className={`watchlist-editor${embedded ? ' embedded' : ''}`}>
      {/* Header */}
      <div className={embedded ? 'mb-4' : 'mb-8'}>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
          <div>
            <HeadingTag className={`${embedded ? 'text-2xl' : 'text-3xl'} font-bold text-gray-900`}>
              Watchlists
            </HeadingTag>
            <p className={`${embedded ? 'text-sm text-gray-500' : 'text-gray-600 text-sm'} mt-1`}>
              Using <strong>{watchlistUsage}</strong> of <strong>{watchlistLimit}</strong> watchlists
              {isAtLimit && ' (limit reached)'}
            </p>
          </div>
          {!isAtLimit ? (
            <Button
              onClick={() => setShowForm(true)}
              size={actionButtonSize}
              className="flex items-center gap-2"
            >
              <FiPlus />
              New Watchlist
            </Button>
          ) : (
            <Button
              onClick={() => navigate('/dashboard/profile')}
              size={actionButtonSize}
              className="flex items-center gap-2 text-blue-600 border border-blue-600 bg-white hover:bg-blue-50"
              variant="outline"
            >
              <FiPlus />
              Upgrade Plan
            </Button>
          )}
        </div>
        <p className="text-gray-600">Create and manage your custom asset watchlists</p>
      </div>

      {isAtLimit && !editingId && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded mb-6 flex items-start gap-3">
          <FiAlertCircle className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Watchlist limit reached</p>
            <p className="text-sm mt-1">You've reached the {watchlistLimit}-watchlist limit for your plan. When you create a new watchlist, the oldest one will be automatically removed. Remove watchlists manually or upgrade to create more.</p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-6 py-4 rounded-r-lg mb-6 shadow-sm">
          <div className="flex items-center gap-2">
            <FiAlertCircle className="flex-shrink-0" />
            <span className="font-medium">{error}</span>
          </div>
        </div>
      )}

      {/* Watchlists List */}
      <div className="space-y-4 mb-8">
        {watchlists.map((watchlist) => (
          <div key={watchlist.id} className="border-2 border-gray-300 rounded-lg p-4 hover:shadow-lg transition-all bg-white hover:border-green-500 shadow-sm">
            {/* Header */}
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <h3 className="font-semibold text-base text-gray-900 mb-1">{watchlist.name}</h3>
                {watchlist.description && (
                  <p className="text-gray-600 text-sm mb-2">{watchlist.description}</p>
                )}
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs bg-blue-50 text-blue-700">
                    {getAssetClassLabel(watchlist.asset_class)}
                  </Badge>
                  <Badge variant="secondary" className="text-xs bg-green-50 text-green-700">
                    {watchlist.symbols.length} {watchlist.symbols.length === 1 ? 'symbol' : 'symbols'}
                  </Badge>
                  <span className="text-xs text-gray-500">
                    Updated {new Date(watchlist.updated_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(watchlist)}
                  className="text-xs hover:bg-blue-50 hover:border-blue-400"
                >
                  <FiEdit2 className="mr-1 h-3 w-3" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDuplicate(watchlist)}
                  className="text-xs hover:bg-green-50 hover:border-green-400"
                >
                  <FiCopy className="mr-1 h-3 w-3" />
                  Duplicate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(watchlist.id)}
                  className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-400"
                >
                  <FiTrash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>

            {/* Symbols */}
            <div className="pt-3 border-t border-gray-200">
              {watchlist.symbols.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {watchlist.symbols.map((symbol) => (
                    <Badge key={symbol.ticker} variant="secondary" className="text-xs px-2 py-1 font-mono bg-gray-100 hover:bg-gray-200">
                      {symbol.ticker}
                    </Badge>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <p className="text-gray-500 text-sm">No symbols added yet</p>
                </div>
              )}
            </div>
          </div>
        ))}

        {watchlists.length === 0 && (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center bg-gray-50">
            <div className="text-gray-400 mb-3">
              <FiList className="mx-auto h-12 w-12" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No watchlists created yet</h3>
            <p className="text-gray-500 text-sm mb-4">Create your first watchlist to track your favorite assets</p>
            <Button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 mx-auto"
            >
              <FiPlus className="h-4 w-4" />
              Create Your First Watchlist
            </Button>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>
                  {editingId ? 'Edit Watchlist' : 'Create New Watchlist'}
                </CardTitle>
                <Button variant="ghost" onClick={resetForm}>
                  <FiX />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="overflow-y-auto">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Input
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="asset-class">Asset Class</Label>
                    <select
                      id="asset-class"
                      value={normalizeAssetClass(formData.asset_class)}
                      onChange={(e) => {
                        const nextClass = normalizeAssetClass(e.target.value);
                        setFormData(prev => ({
                          ...prev,
                          asset_class: nextClass,
                          symbols: []
                        }));
                        setSearchTerm('');
                        setBulkSymbolsInput('');
                        setBulkMissing([]);
                      }}
                      className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {ASSET_CLASS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Symbol Search */}
                <div>
                  <Label>Add Symbols</Label>
                  <div className="mt-2 mb-4">
                    <Label htmlFor="bulk-symbols" className="text-sm">Paste symbols (comma or space-separated)</Label>
                    <div className="flex flex-col md:flex-row md:items-start gap-2 mt-2">
                      <textarea
                        id="bulk-symbols"
                        rows={3}
                        placeholder="RKLB,BILL,BKV,PLTR,DDOG,DELL,CRM,NVDA"
                        value={bulkSymbolsInput}
                        onChange={(e) => {
                          setBulkSymbolsInput(e.target.value);
                          if (bulkMissing.length > 0) setBulkMissing([]);
                        }}
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <Button type="button" onClick={handleBulkAdd} className="md:mt-0">
                        Add All
                      </Button>
                    </div>
                    {bulkMissing.length > 0 && (
                      <div className="mt-2 text-sm text-red-600 flex items-start gap-2">
                        <FiAlertCircle className="mt-0.5" />
                        <span>
                          <span className="font-medium">Missing symbols:</span> {bulkMissing.join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="relative mb-4">
                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <Input
                      placeholder="Search symbols by ticker, company name, or sector..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  
                  <div className="max-h-60 overflow-y-auto border rounded-lg">
                    {filteredSymbols.slice(0, 50).map((symbol) => (
                      <div
                        key={symbol.ticker}
                        className="flex items-center justify-between p-3 hover:bg-gray-50 border-b last:border-b-0"
                      >
                        <div>
                          <div className="font-medium">{symbol.ticker}</div>
                          {(symbol.company_name || symbol.name) && (
                            <div className="text-sm text-gray-600">{symbol.company_name || symbol.name}</div>
                          )}
                          {symbol.sector && (
                            <div className="text-xs text-gray-500">{symbol.sector}</div>
                          )}
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => addSymbol(symbol)}
                          disabled={formData.symbols.find(s => s.ticker === symbol.ticker)}
                        >
                          <FiPlus />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Selected Symbols */}
                <div>
                  <Label>Selected Symbols ({formData.symbols.length})</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.symbols.map((symbol) => (
                      <Badge key={symbol.ticker} variant="default" className="flex items-center gap-1">
                        {symbol.ticker}
                        <button
                          type="button"
                          onClick={() => removeSymbol(symbol.ticker)}
                          className="ml-1 hover:text-red-500"
                        >
                          <FiX size={12} />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading}>
                    <FiSave className="mr-2" />
                    {loading ? 'Saving...' : (editingId ? 'Update' : 'Create')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default WatchlistEditor;
